import { useEffect, useRef } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import aimsTheme from "../../../styles/meridian-theme-aims.json";
import AgentErrorMessage from "./errors/AgentErrorMessage";
import InMeetingExpFeedback from "../InMeetingExpFeedback";
import CARD_RENDERERS from "./customBlocks/cardRenderers";
import markdownComponents from "./markdownComponents";
import { BUILT_IN_AGENT } from "../../../constants";
import { THEME_COLOR } from "../../../styles/colors";
import { PROMPT_TYPE } from "../assistant.constants";
import { WHOS_WHO_STATE } from "../../whosWho/whosWho.constants";
import { parseCustomBlocks } from "./customBlocks/customBlocks.utils";
import AgentAvatar from "./AgentAvatar";
import { truncateDisplayName } from "../../../utils/agentDisplayUtils";
import WhosWhoPanel from "../../whosWho/WhosWhoPanel";
import StartedByStatus from "../../../components/StartedByStatus";

// Parse a message and drop any card the user dismissed. `scopeKey` namespaces the
// keys per message so identical cards in different messages stay independent.
const getVisibleContent = (text, { isInternal, scopeKey, dismissedCards }) => {
  const { segments, incompleteBlock } = parseCustomBlocks(text, { isInternal });
  // Two different keys on purpose: the React key stays positional so a streaming
  // message doesn't remount its markdown on every delta, while the dismiss key is
  // content-based so re-publishing can't shift a dismissal onto a different card.
  const visible = segments
    .map((segment, i) => ({
      segment,
      cardKey: `${scopeKey}#${i}`,
      dismissKey: `${scopeKey}#${JSON.stringify(segment)}`,
    }))
    .filter(({ dismissKey }) => !dismissedCards?.has(dismissKey));
  return {
    visibleSegments: visible,
    incompleteBlock,
    isEmpty: visible.length === 0 && !incompleteBlock,
  };
};

const AgentMessage = ({ isError, errorMessage, visibleSegments, incompleteBlock, onDismissCard }) => {
  if (isError) {
    return <AgentErrorMessage errorMessage={errorMessage} />;
  }

  return (
    <Column spacing="400" spacingInset="300 none">
      {/* Keyed by cardKey (the pre-filter index), not the map index: dismissing one
          card must not shift its siblings' keys, or React remounts them and cards
          with local state (expanders) silently reset. */}
      {visibleSegments.map(({ segment, cardKey, dismissKey }) => {
        if (segment.type === "text") {
          return (
            <ReactMarkdown
              key={cardKey}
              remarkPlugins={[remarkBreaks, remarkGfm]}
              components={markdownComponents}
            >
              {segment.content}
            </ReactMarkdown>
          );
        }
        const renderer = CARD_RENDERERS[segment.cardType];
        return renderer
          ? renderer(segment, cardKey, { onDismiss: () => onDismissCard?.(dismissKey) })
          : null;
      })}
      {incompleteBlock && CARD_RENDERERS[incompleteBlock.cardType]?.(incompleteBlock, "streaming")}
    </Column>
  );
};

const PublishedContentItem = ({ item, enabledAgents, dismissedCards, onDismissCard }) => {
  const agent = enabledAgents.find((a) => a.id === item.extensionId) || { id: item.extensionId, name: item.displayName || item.extensionId, shortName: item.shortName };
  const { visibleSegments, incompleteBlock, isEmpty } = getVisibleContent(item.content || "", {
    isInternal: false,
    scopeKey: `published:${item.key}`,
    dismissedCards,
  });

  // Nothing left to show — drop the avatar and name with it.
  if (isEmpty) return null;

  return (
    <Column spacing="none" spacingInset="none none 400 none">
      <Row spacing="200" alignmentVertical="center">
        <AgentAvatar agent={agent} size={22} />
        <Text type="b100" color="secondary">{truncateDisplayName(agent.name)}</Text>
      </Row>
      <Column spacingInset="100 none none none">
        <AgentMessage
          isError={false}
          visibleSegments={visibleSegments}
          incompleteBlock={incompleteBlock}
          onDismissCard={onDismissCard}
        />
      </Column>
    </Column>
  );
};

// `startedBy` (the "started by" status) is placed in the timeline at the time it appeared.
const ChatMessages = ({ messages, isLoading, sessionId, aimsUuid, meetingId, meetingUuid, onFeedbackMessage, onOpenFeedbackPopover, enabledAgents = [], publishedContentStore, startedBy }) => {
  const isWhosWhoPending = messages.some((msg) => msg.whosWho && msg.isLoading);

  const bottomRef = useRef(null);
  const publishedItems = publishedContentStore?.contentList || [];
  const dismissedCards = publishedContentStore?.dismissedCards;
  const dismissCard = publishedContentStore?.dismissCard;

  // Merge published content and chat messages into a single timeline sorted by
  // timestamp (ms). Chat messages carry `createdAt`; published items carry
  // `publishedAt`. Entries without a timestamp are dropped — every real message
  // is stamped at creation, so a missing timestamp means malformed data that we
  // can't place chronologically. The `order` tiebreaker preserves insertion
  // order among entries with equal ts.
  const entries = [];
  publishedItems.forEach((item) => {
    if (!item.publishedAt) return;
    entries.push({ kind: "published", ts: item.publishedAt, item });
  });
  messages.forEach((msg, idx) => {
    if (!msg.createdAt) return;
    entries.push({ kind: "chat", ts: msg.createdAt, msg, idx });
  });
  if (startedBy) entries.push({ kind: "startedBy", ts: startedBy.shownAt });
  const timeline = entries
    .map((entry, order) => ({ ...entry, order }))
    .sort((a, b) => (a.ts - b.ts) || (a.order - b.order));

  // Scroll to the bottom when something arrives: a message, a card or an in-place card update
  // (`contents`, not the count), or the "started by" status. Dismissing a card doesn't scroll.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, publishedContentStore?.contents, startedBy?.shownAt]);

  return (
    <Column
      spacing="none"
      width="318px"
      overflowY="auto"
      overflowX="hidden"
      spacingInset="400 none"
    >
      {timeline.map((entry) => {
        if (entry.kind === "startedBy") return <StartedByStatus key="startedBy" startedBy={startedBy} />;
        if (entry.kind === "published") {
          return (
            <PublishedContentItem
              key={entry.item.key}
              item={entry.item}
              enabledAgents={enabledAgents}
              dismissedCards={dismissedCards}
              onDismissCard={dismissCard}
            />
          );
        }

        const { msg, idx } = entry;
        if (!msg.text && !msg.isError && !msg.whosWho) return null;
        const prevMsg = messages[idx - 1];
        const agent = msg.agent || BUILT_IN_AGENT;
        const isAgentMsg = msg.role !== "user";
        const content = isAgentMsg && !msg.isError && !msg.whosWho
          ? getVisibleContent(msg.text || "", {
              isInternal: agent.id === BUILT_IN_AGENT.id,
              scopeKey: `chat:${msg.createdAt}:${idx}`,
              dismissedCards,
            })
          : null;

        // Fully dismissed agent turn: drop the avatar and feedback controls too, not
        // just the body.
        if (content?.isEmpty) return null;

        return (
          <Column key={`chat-${idx}`} spacing="none">
            {msg.role === "user" ? (
              <Row alignmentHorizontal="end" spacing="none">
                <Theme tokens={{ boxBorderRadiusFill: 8, boxBorderWidth: 0 }}>
                  <Box
                    type="fill"
                    backgroundColor={THEME_COLOR.MINT_GREEN}
                    spacingInset="350 400 350 400"
                    maxWidth="250px"
                  >
                    <Text type="b100" breakWord>{msg.text}</Text>
                  </Box>
                </Theme>
              </Row>
            ) : (
              <>
                <Row spacing="200" alignmentVertical="center">
                  <AgentAvatar agent={agent} size={22} />
                  <Text type="b100" color="secondary">{truncateDisplayName(agent.name)}</Text>
                </Row>
                <Row height="4px" />
                {msg.whosWho ? (
                  <WhosWhoPanel
                    state={msg.isLoading ? WHOS_WHO_STATE.LOADING : msg.isError ? WHOS_WHO_STATE.ERROR : WHOS_WHO_STATE.LOADED}
                    invitees={msg.invitees || []}
                  />
                ) : (
                  <AgentMessage
                    isError={msg.isError}
                    errorMessage={msg.errorMessage}
                    visibleSegments={content?.visibleSegments || []}
                    incompleteBlock={content?.incompleteBlock}
                    onDismissCard={dismissCard}
                  />
                )}
                {msg.timestamp && msg.text !== "(No response)" && (
                  <InMeetingExpFeedback
                    agent={agent}
                    sessionId={sessionId}
                    aimsUuid={aimsUuid}
                    messageTimestamp={msg.timestamp}
                    promptType={prevMsg?.promptType || PROMPT_TYPE.FREEFORM}
                    userInput={prevMsg?.text || ""}
                    agentResponse={msg.text}
                    extensionId={msg.agentId}
                    meetingId={meetingId}
                    meetingUuid={meetingUuid}
                    onFeedbackMessage={onFeedbackMessage}
                    onOpenFeedbackPopover={onOpenFeedbackPopover}
                    streamingDisabled={isLoading}
                  />
                )}
              </>
            )}
            <Row height="16px" />
          </Column>
        );
      })}

      {isLoading && !isWhosWhoPending && (
        <Row alignmentHorizontal="start" spacing="none">
          <Box spacingInset="300">
            <Theme palette={aimsTheme}>
              <Loader size="small" />
            </Theme>
          </Box>
        </Row>
      )}

      <Row ref={bottomRef} height="1px" />
    </Column>
  );
};

export default ChatMessages;
